import React, { useEffect, useMemo, useState } from 'react';
import {
  Award, Plus, Pencil, Trash2, Loader2, Save, X, Download, CheckCircle2,
  ShieldCheck, RotateCcw, FileText, User, Calendar,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const emptyForm = () => ({
  id: null,
  intern_type: 'agent',
  agent_id: '',
  intern_name: '',
  intern_birth_date: '',
  intern_birth_place: '',
  intern_id_number: '',
  internship_start_date: '',
  internship_end_date: '',
  training_ids: [],
  custom_modules: [],
  appreciation: '',
  director_name: '',
  director_title: 'Le Directeur',
});

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR');
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) =>
  status === 'validated' ? (
    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="status-validated">
      <CheckCircle2 className="w-3 h-3 mr-1" /> Validée
    </Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-testid="status-draft">
      <FileText className="w-3 h-3 mr-1" /> Brouillon
    </Badge>
  );

const InternshipCertificates = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [trainings, setTrainings] = useState([]);

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, ag, tr] = await Promise.all([
        api.get('/internship-certificates'),
        api.get('/internship-certificates/available-agents'),
        api.get('/internship-certificates/available-trainings'),
      ]);
      setItems(list.data || []);
      setAgents(ag.data || []);
      setTrainings(tr.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const trainingsById = useMemo(() => {
    const m = {};
    trainings.forEach(t => { m[t.id] = t; });
    return m;
  }, [trainings]);

  const openCreate = () => setEditing(emptyForm());
  const openEdit = (c) => setEditing({ ...emptyForm(), ...c, custom_modules: c.custom_modules || [], training_ids: c.training_ids || [] });
  const cancelEdit = () => setEditing(null);

  const toggleTraining = (id) => {
    setEditing(prev => {
      const has = prev.training_ids.includes(id);
      return {
        ...prev,
        training_ids: has ? prev.training_ids.filter(x => x !== id) : [...prev.training_ids, id],
      };
    });
  };

  const addCustomModule = () => setEditing(prev => ({
    ...prev,
    custom_modules: [...prev.custom_modules, { name: '', description: '', validated_date: '' }],
  }));
  const updateCustomModule = (i, patch) => setEditing(prev => ({
    ...prev,
    custom_modules: prev.custom_modules.map((m, idx) => idx === i ? { ...m, ...patch } : m),
  }));
  const removeCustomModule = (i) => setEditing(prev => ({
    ...prev,
    custom_modules: prev.custom_modules.filter((_, idx) => idx !== i),
  }));

  const validateForm = () => {
    if (!editing.internship_start_date || !editing.internship_end_date) {
      toast.error('Veuillez renseigner la période du stage'); return false;
    }
    if (editing.intern_type === 'agent' && !editing.agent_id) {
      toast.error('Veuillez sélectionner un agent'); return false;
    }
    if (editing.intern_type === 'external' && !editing.intern_name.trim()) {
      toast.error('Veuillez saisir le nom du stagiaire externe'); return false;
    }
    if (editing.training_ids.length === 0 && editing.custom_modules.length === 0) {
      toast.error('Veuillez cocher au moins une formation ou ajouter une compétence'); return false;
    }
    return true;
  };

  const save = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        intern_type: editing.intern_type,
        agent_id: editing.intern_type === 'agent' ? editing.agent_id : null,
        intern_name: editing.intern_type === 'external' ? editing.intern_name : null,
        intern_birth_date: editing.intern_birth_date || null,
        intern_birth_place: editing.intern_birth_place || null,
        intern_id_number: editing.intern_id_number || null,
        internship_start_date: editing.internship_start_date,
        internship_end_date: editing.internship_end_date,
        training_ids: editing.training_ids,
        custom_modules: editing.custom_modules.filter(m => (m.name || '').trim()),
        appreciation: editing.appreciation || '',
        director_name: editing.director_name || null,
        director_title: editing.director_title || 'Le Directeur',
      };
      if (editing.id) {
        await api.put(`/internship-certificates/${editing.id}`, payload);
        toast.success('Attestation mise à jour');
      } else {
        await api.post('/internship-certificates', payload);
        toast.success('Attestation créée en brouillon');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const doValidate = async (c) => {
    if (!window.confirm(`Valider l'attestation ${c.certificate_number} ? Elle ne sera plus modifiable.`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/internship-certificates/${c.id}/validate`);
      toast.success('Attestation validée');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  const doReopen = async (c) => {
    if (!window.confirm(`Rouvrir l'attestation ${c.certificate_number} en brouillon ?`)) return;
    setBusyId(c.id);
    try {
      await api.post(`/internship-certificates/${c.id}/reopen`);
      toast.success('Attestation remise en brouillon');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await api.delete(`/internship-certificates/${confirmDelete.id}`);
      toast.success('Attestation supprimée');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusyId(null);
    }
  };

  const doDownload = async (c) => {
    setBusyId(c.id);
    try {
      const r = await api.get(`/internship-certificates/${c.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `attestation_stage_${c.certificate_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
      toast.success('Attestation téléchargée');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de téléchargement');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

  return (
    <div className="space-y-6" data-testid="internship-certificates-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1B3A4B] flex items-center gap-2">
            <Award className="w-7 h-7 text-amber-600" /> Attestations de Stage
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Délivrez une attestation officielle à un agent ou stagiaire après validation des modules par le super-administrateur.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="create-certificate-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle attestation
        </Button>
      </div>

      {/* Empty state */}
      {items.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Award className="w-14 h-14 mx-auto text-amber-300 mb-3" />
            <p className="text-gray-500 mb-4">Aucune attestation de stage pour l&apos;instant.</p>
            <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Créer la première attestation
            </Button>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.map((c) => {
          const trainingCount = (c.training_ids || []).length;
          const customCount = (c.custom_modules || []).length;
          return (
            <Card key={c.id} className="border-2 border-gray-100 hover:border-amber-300 transition-colors" data-testid={`cert-card-${c.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-amber-700 uppercase tracking-wider">
                      {c.certificate_number}
                    </div>
                    <h3 className="text-lg font-bold text-[#1B3A4B] flex items-center gap-2 truncate">
                      <User className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      {c._display_name || c.intern_name || '—'}
                    </h3>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Type : {c.intern_type === 'agent' ? 'Agent interne' : 'Stagiaire externe'}
                    </div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase">Du</div>
                    <div className="font-semibold text-[#1B3A4B] flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-amber-600" /> {fmtDate(c.internship_start_date)}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-[10px] font-semibold text-gray-500 uppercase">Au</div>
                    <div className="font-semibold text-[#1B3A4B] flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-amber-600" /> {fmtDate(c.internship_end_date)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-100">
                    {trainingCount} formation(s)
                  </Badge>
                  <Badge className="bg-amber-50 text-amber-700 border border-amber-100">
                    {customCount} compétence(s)
                  </Badge>
                </div>

                {c.status === 'validated' && (
                  <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5 border border-emerald-100">
                    <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
                    Validée par <b>{c.validated_by_name || '—'}</b> le {fmtDate(c.validated_at)}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                  {c.status === 'draft' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => doValidate(c)} disabled={busyId === c.id}
                                data-testid={`validate-${c.id}`}>
                          {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                          Valider (Super-Admin)
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => setConfirmDelete(c)} data-testid={`delete-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
                      </Button>
                    </>
                  )}
                  {c.status === 'validated' && (
                    <>
                      <Button size="sm" onClick={() => doDownload(c)} disabled={busyId === c.id}
                              className="bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`download-${c.id}`}>
                        {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                        Télécharger PDF
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" variant="outline" onClick={() => doReopen(c)}
                                data-testid={`reopen-${c.id}`}>
                          <RotateCcw className="w-3.5 h-3.5 mr-1" /> Rouvrir
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && cancelEdit()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cert-form-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              {editing?.id ? 'Modifier l\'attestation' : 'Nouvelle attestation de stage'}
            </DialogTitle>
            <DialogDescription>
              Renseignez les modules validés. L&apos;attestation restera en brouillon jusqu&apos;à validation par le super-administrateur.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-5 py-2">
              {/* Intern type */}
              <div>
                <Label>Type de stagiaire *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, intern_type: 'agent' })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${editing.intern_type === 'agent' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid="intern-type-agent"
                  >
                    <div className="font-semibold text-sm text-[#1B3A4B]">Agent interne</div>
                    <div className="text-xs text-gray-500">Sélectionner un agent avec compte</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing({ ...editing, intern_type: 'external' })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${editing.intern_type === 'external' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
                    data-testid="intern-type-external"
                  >
                    <div className="font-semibold text-sm text-[#1B3A4B]">Stagiaire externe</div>
                    <div className="text-xs text-gray-500">Nom libre, sans compte utilisateur</div>
                  </button>
                </div>
              </div>

              {/* Intern identity */}
              {editing.intern_type === 'agent' ? (
                <div>
                  <Label>Agent *</Label>
                  <Select value={editing.agent_id || ''} onValueChange={(v) => setEditing({ ...editing, agent_id: v })}>
                    <SelectTrigger data-testid="select-agent"><SelectValue placeholder="Sélectionner un agent" /></SelectTrigger>
                    <SelectContent>
                      {agents.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name} {a.employee_id ? `(${a.employee_id})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <Label>Nom complet du stagiaire *</Label>
                    <Input value={editing.intern_name} onChange={(e) => setEditing({ ...editing, intern_name: e.target.value })}
                           placeholder="Ex : Aminata Diallo" data-testid="intern-name" />
                  </div>
                  <div>
                    <Label>Date de naissance</Label>
                    <Input type="date" value={editing.intern_birth_date}
                           onChange={(e) => setEditing({ ...editing, intern_birth_date: e.target.value })} />
                  </div>
                  <div>
                    <Label>Lieu de naissance</Label>
                    <Input value={editing.intern_birth_place}
                           onChange={(e) => setEditing({ ...editing, intern_birth_place: e.target.value })}
                           placeholder="Ex : Bamako" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>N° pièce d&apos;identité</Label>
                    <Input value={editing.intern_id_number}
                           onChange={(e) => setEditing({ ...editing, intern_id_number: e.target.value })} />
                  </div>
                </div>
              )}

              {/* Period */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Début du stage *</Label>
                  <Input type="date" value={editing.internship_start_date}
                         onChange={(e) => setEditing({ ...editing, internship_start_date: e.target.value })}
                         data-testid="stage-start" />
                </div>
                <div>
                  <Label>Fin du stage *</Label>
                  <Input type="date" value={editing.internship_end_date}
                         onChange={(e) => setEditing({ ...editing, internship_end_date: e.target.value })}
                         data-testid="stage-end" />
                </div>
              </div>

              {/* Trainings checklist */}
              <div>
                <Label className="flex items-center gap-2">
                  Formations complétées ({editing.training_ids.length}/{trainings.length})
                </Label>
                {trainings.length === 0 ? (
                  <div className="text-xs text-gray-400 italic p-3 border border-dashed rounded-lg mt-1.5">
                    Aucune formation disponible. Créez-en depuis le module Formation.
                  </div>
                ) : (
                  <div className="mt-1.5 border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-gray-100" data-testid="training-checklist">
                    {trainings.map(t => (
                      <label key={t.id} className="flex items-start gap-2 p-2.5 hover:bg-gray-50 cursor-pointer">
                        <Checkbox
                          checked={editing.training_ids.includes(t.id)}
                          onCheckedChange={() => toggleTraining(t.id)}
                          data-testid={`training-check-${t.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#1B3A4B]">{t.title}</div>
                          <div className="text-[11px] text-gray-500">{t.category}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Custom modules */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Compétences pratiques validées ({editing.custom_modules.length})</Label>
                  <Button size="sm" variant="outline" onClick={addCustomModule} data-testid="add-custom-module">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
                  </Button>
                </div>
                <div className="mt-2 space-y-2">
                  {editing.custom_modules.map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start p-2 border border-gray-200 rounded-lg">
                      <Input
                        className="col-span-12 md:col-span-5"
                        placeholder="Ex : Rédaction de bail"
                        value={m.name}
                        onChange={(e) => updateCustomModule(i, { name: e.target.value })}
                        data-testid={`custom-name-${i}`}
                      />
                      <Input
                        className="col-span-8 md:col-span-4"
                        placeholder="Description (optionnel)"
                        value={m.description}
                        onChange={(e) => updateCustomModule(i, { description: e.target.value })}
                      />
                      <Input
                        type="date"
                        className="col-span-4 md:col-span-2"
                        value={m.validated_date}
                        onChange={(e) => updateCustomModule(i, { validated_date: e.target.value })}
                      />
                      <Button size="sm" variant="ghost" className="col-span-12 md:col-span-1 text-red-600"
                              onClick={() => removeCustomModule(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Appreciation */}
              <div>
                <Label>Appréciation générale</Label>
                <Textarea
                  rows={4}
                  value={editing.appreciation}
                  onChange={(e) => setEditing({ ...editing, appreciation: e.target.value })}
                  placeholder="Ponctuel, appliqué, esprit d'équipe, motivé..."
                  data-testid="appreciation"
                />
              </div>

              {/* Signature */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom du signataire</Label>
                  <Input value={editing.director_name}
                         onChange={(e) => setEditing({ ...editing, director_name: e.target.value })}
                         placeholder="Nom du Directeur" />
                </div>
                <div>
                  <Label>Titre / Fonction</Label>
                  <Input value={editing.director_title}
                         onChange={(e) => setEditing({ ...editing, director_title: e.target.value })}
                         placeholder="Le Directeur" />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-cert-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</>
                      : <><Save className="w-4 h-4 mr-2" /> Enregistrer en brouillon</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="delete-modal">
          <DialogHeader>
            <DialogTitle>Supprimer cette attestation ?</DialogTitle>
            <DialogDescription>
              L&apos;attestation <b>{confirmDelete?.certificate_number}</b> sera supprimée définitivement.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuler</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doDelete}
                    disabled={busyId === confirmDelete?.id}>
              {busyId === confirmDelete?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InternshipCertificates;
